// frontend/src/pages/PoliticaPublicidade.jsx
import { useState } from "react";
import { ArrowLeft, Download } from "lucide-react";

export default function PoliticaPublicidade() {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const pdfUrl = `${process.env.PUBLIC_URL}/pdfs/politica-publicidade.pdf`;

  const handleDownloadPDF = () => {
    setIsGeneratingPDF(true);
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'politica-publicidade.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsGeneratingPDF(false);
  };

  return (
    <>
      <style>
        {`
          @media print {
            body { margin: 0; padding: 0; background: white; }
            .no-print { display: none !important; }
            .print-content { margin: 0; padding: 0.5cm; width: 100%; }
            .print-content h1 { font-size: 18pt; margin-bottom: 10pt; }
            .print-content h2 { font-size: 14pt; margin-top: 15pt; margin-bottom: 8pt; page-break-after: avoid; }
            .print-content p { font-size: 10pt; line-height: 1.4; margin-bottom: 6pt; page-break-inside: avoid; }
            .print-content ul { margin: 0 0 8pt 0; padding-left: 20pt; }
            .print-content li { font-size: 10pt; margin-bottom: 3pt; page-break-inside: avoid; }
            .print-content .section { page-break-inside: avoid; margin-bottom: 12pt; }
            .print-content .border-b { border-bottom: 0.5pt solid #ccc; }
            @page { size: A4; margin: 1.5cm; }
          }
        `}
      </style>

      <div className="min-h-screen bg-gray-50 pt-2 pb-12">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="no-print bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img
                  src={`${process.env.PUBLIC_URL}/img/logo2.png`}
                  alt="Brechó Vizinho"
                  className="h-12 w-auto object-contain"
                />
                <div>
                  <h1 className="text-xl font-bold text-gray-800">Política de Publicidade</h1>
                  <p className="text-xs text-gray-500">Versão 1.0 | 15/06/2026</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => window.history.back()}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Voltar</span>
                </button>
                <button
                  onClick={handleDownloadPDF}
                  disabled={isGeneratingPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-[#449879] text-white rounded-lg hover:bg-[#2d5f4d] transition-colors disabled:opacity-50"
                >
                  {isGeneratingPDF ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Baixar PDF</span>
                </button>
              </div>
            </div>
          </div>

          {/* Conteúdo */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 print:shadow-none print:p-0 print:bg-white">
            <div className="print-content prose prose-sm max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-strong:text-gray-800">
              
              {/* TÍTULO */}
              <div className="text-center mb-8 print:mb-4">
                <h1 className="text-2xl font-bold text-gray-800 mb-2">POLÍTICA DE PUBLICIDADE</h1>
                <p className="text-sm text-gray-500">Versão 1.0 | Data de Vigência: 15/06/2026</p>
                <div className="w-20 h-1 bg-[#449879] mx-auto mt-3 rounded-full print:bg-gray-400" />
              </div>

              {/* INTRODUÇÃO */}
              <p className="text-gray-600 leading-relaxed mb-4">
                O Brechó Vizinho disponibiliza espaços publicitários destinados à divulgação de produtos, serviços, negócios
                locais, empreendedores da comunidade e empresas parceiras.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                Esta Política estabelece as regras aplicáveis à veiculação de conteúdos publicitários na plataforma.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Ao contratar ou utilizar espaços publicitários, o anunciante declara estar de acordo com as disposições aqui
                descritas.
              </p>

              {/* 1. OBJETIVO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">1. OBJETIVO</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A publicidade no Brechó Vizinho tem como finalidade:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Apoiar empreendedores locais;</li>
                  <li>Dar visibilidade a prestadores de serviços;</li>
                  <li>Promover negócios da comunidade;</li>
                  <li>Divulgar empresas parceiras;</li>
                  <li>Contribuir para a sustentabilidade financeira da plataforma.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  Nosso objetivo é fortalecer a economia local e aproximar moradores de produtos e serviços de interesse
                  da comunidade.
                </p>
              </div>

              {/* 2. QUEM PODE ANUNCIAR */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">2. QUEM PODE ANUNCIAR</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Poderão anunciar na plataforma:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Moradores empreendedores;</li>
                  <li>Profissionais autônomos;</li>
                  <li>Prestadores de serviços;</li>
                  <li>Pequenos negócios locais;</li>
                  <li>Empresas parceiras;</li>
                  <li>Patrocinadores da comunidade;</li>
                  <li>Organizações legalmente constituídas.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  O Brechó Vizinho poderá solicitar informações adicionais para validação do anunciante.
                </p>
              </div>

              {/* 3. FORMATOS DE PUBLICIDADE */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">3. FORMATOS DE PUBLICIDADE</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A plataforma poderá disponibilizar diferentes formatos publicitários, incluindo:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Banners;</li>
                  <li>Destaques patrocinados;</li>
                  <li>Anúncios promocionais;</li>
                  <li>Espaços de patrocinadores;</li>
                  <li>Conteúdo institucional;</li>
                  <li>Campanhas especiais;</li>
                  <li>Outras modalidades que venham a ser criadas.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  Os formatos poderão ser alterados, ampliados ou descontinuados a qualquer momento.
                </p>
              </div>

              {/* 4. ANÚNCIOS PATROCINADOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">4. ANÚNCIOS PATROCINADOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Anúncios patrocinados são conteúdos promovidos mediante contratação de espaço publicitário.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Esses conteúdos poderão receber identificação visual específica para diferenciá-los de conteúdos orgânicos
                  da comunidade.
                </p>
              </div>

              {/* 5. AUSÊNCIA DE RECOMENDAÇÃO OU GARANTIA */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">5. AUSÊNCIA DE RECOMENDAÇÃO OU GARANTIA</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A exibição de anúncios, patrocinadores ou conteúdos promocionais não representa:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Recomendação;</li>
                  <li>Certificação;</li>
                  <li>Garantia;</li>
                  <li>Avaliação técnica;</li>
                  <li>Endosso comercial.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho não realiza validação técnica dos produtos ou serviços anunciados.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  A contratação de publicidade não implica qualquer aprovação, recomendação ou chancela por parte da
                  plataforma.
                </p>
              </div>

              {/* 6. RESPONSABILIDADE DOS ANUNCIANTES */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">6. RESPONSABILIDADE DOS ANUNCIANTES</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Os anunciantes são integralmente responsáveis por:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Informações divulgadas;</li>
                  <li>Produtos anunciados;</li>
                  <li>Serviços oferecidos;</li>
                  <li>Condições comerciais;</li>
                  <li>Garantias eventualmente prometidas;</li>
                  <li>Cumprimento da legislação aplicável.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  O anunciante declara possuir autorização para divulgar todo o conteúdo publicado.
                </p>
              </div>

              {/* 7. CONTEÚDOS PROIBIDOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">7. CONTEÚDOS PROIBIDOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Não serão aceitos anúncios relacionados a:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Atividades ilegais;</li>
                  <li>Produtos proibidos por lei;</li>
                  <li>Armas e munições;</li>
                  <li>Drogas ilícitas;</li>
                  <li>Produtos falsificados;</li>
                  <li>Material pornográfico;</li>
                  <li>Jogos de azar ilegais;</li>
                  <li>Esquemas fraudulentos;</li>
                  <li>Conteúdo discriminatório;</li>
                  <li>Conteúdo ofensivo ou incompatível com os valores da comunidade.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  O Brechó Vizinho poderá rejeitar ou remover anúncios sem necessidade de justificativa prévia.
                </p>
              </div>

              {/* 8. REVISÃO E MODERAÇÃO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">8. REVISÃO E MODERAÇÃO</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho reserva-se o direito de analisar previamente ou posteriormente qualquer conteúdo
                  publicitário.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A plataforma poderá:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Solicitar ajustes;</li>
                  <li>Suspender campanhas;</li>
                  <li>Remover anúncios;</li>
                  <li>Encerrar contratos publicitários.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  Sempre que entender necessário para proteção da comunidade ou cumprimento da legislação.
                </p>
              </div>

              {/* 9. CONTRATAÇÃO DE ESPAÇOS PUBLICITÁRIOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">9. CONTRATAÇÃO DE ESPAÇOS PUBLICITÁRIOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Os espaços publicitários poderão ser contratados mediante pagamento conforme condições vigentes na
                  plataforma.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Os preços, formatos, períodos de exibição e demais condições poderão ser alterados a qualquer momento
                  para novas contratações.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  As alterações não afetarão campanhas já contratadas e em execução.
                </p>
              </div>

              {/* 10. RESULTADOS NÃO GARANTIDOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">10. RESULTADOS NÃO GARANTIDOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A contratação de publicidade, destaque ou patrocínio não garante:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Vendas;</li>
                  <li>Contatos;</li>
                  <li>Leads;</li>
                  <li>Visualizações mínimas;</li>
                  <li>Retorno financeiro;</li>
                  <li>Conversões;</li>
                  <li>Qualquer resultado comercial específico.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  Os resultados dependem de diversos fatores externos ao controle da plataforma.
                </p>
              </div>

              {/* 11. EMPREENDEDORES DA COMUNIDADE */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">11. EMPREENDEDORES DA COMUNIDADE</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho incentiva a participação de moradores empreendedores e pequenos negócios locais.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  No entanto, a participação desses usuários não implica certificação, validação técnica ou garantia de qualidade
                  por parte da plataforma.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  As negociações permanecem sob responsabilidade exclusiva das partes envolvidas.
                </p>
              </div>

              {/* 12. PATROCINADORES */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">12. PATROCINADORES</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A plataforma poderá manter uma área específica para patrocinadores da comunidade.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A presença de patrocinadores não altera a independência editorial ou operacional do Brechó Vizinho.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Patrocinadores não possuem poder de decisão sobre regras, moderação ou funcionamento da plataforma.
                </p>
              </div>

              {/* 13. DENÚNCIAS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">13. DENÚNCIAS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Usuários poderão denunciar anúncios publicitários que considerem inadequados, enganosos ou incompatíveis
                  com esta Política.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  As denúncias serão analisadas pela equipe responsável.
                </p>
              </div>

              {/* 14. ALTERAÇÕES DESTA POLÍTICA */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">14. ALTERAÇÕES DESTA POLÍTICA</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Esta Política poderá ser atualizada periodicamente para acompanhar mudanças operacionais, legais ou
                  comerciais.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  A versão mais recente estará sempre disponível na plataforma.
                </p>
              </div>

              {/* 15. DISPOSIÇÕES FINAIS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">15. DISPOSIÇÕES FINAIS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A utilização ou contratação de espaços publicitários implica concordância com esta Política.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  O Brechó Vizinho reserva-se o direito de interpretar e aplicar estas diretrizes visando proteger a comunidade
                  e a integridade da plataforma.
                </p>
              </div>

              {/* ASSINATURA FINAL */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Última atualização: 15/06/2026<br />
                  Versão: 1.0<br />
                  Brechó Vizinho<br />
                  E-mail: <span className="text-[#449879] font-medium">info@brechovizinho.com.br</span>
                </p>
              </div>
            </div>
          </div>

          {/* Botão voltar ao topo */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="no-print fixed bottom-20 right-4 bg-[#449879] text-white p-3 rounded-full shadow-lg hover:bg-[#2d5f4d] transition-colors z-40"
            aria-label="Voltar ao topo"
          >
            ↑
          </button>
        </div>
      </div>
    </>
  );
}